/**
 * Observables Inspector Panel Component
 *
 * Displays observable definitions with filtering, search, and trace actions.
 * Supports click-to-highlight for dual-view synchronization.
 *
 * @see docs/designs/smart-asset-algebra-visualization.md
 * @see docs/discoveries/2026-01-05-ui-schema-consumption.md
 */
import { memo, useMemo, useState, useCallback } from "react";
import type { Observable } from "../../../types/valueFlows";
import { useHighlightOptional } from "./HighlightContext";

interface ObservablesInspectorPanelProps {
  /** Observables from schema */
  observables?: Record<string, Observable>;
  /** Compact mode for smaller displays */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Get color for observable type
 */
function getTypeColor(type: string): string {
  switch (type) {
    case "boolean":
      return "text-sa-success";
    case "number":
      return "text-primary";
    case "string":
      return "text-purple-400";
    case "date":
      return "text-sa-warning";
    default:
      return "text-sa-text-dim";
  }
}

/**
 * Get glyph icon for observable type
 */
function getTypeGlyph(type: string): string {
  switch (type) {
    case "boolean":
      return "✓";
    case "number":
      return "#";
    case "string":
      return "T";
    case "date":
      return "⏱";
    default:
      return "•";
  }
}

/**
 * Badge color mapping for ui_hint.badge_color
 */
function getBadgeColorClasses(color?: string): string {
  switch (color) {
    case "green":
      return "bg-sa-success/20 text-sa-success border-sa-success/30";
    case "red":
      return "bg-sa-danger/20 text-sa-danger border-sa-danger/30";
    case "blue":
      return "bg-primary/20 text-primary border-primary/30";
    case "amber":
    case "yellow":
      return "bg-sa-warning/20 text-sa-warning border-sa-warning/30";
    case "purple":
      return "bg-purple-500/20 text-purple-400 border-purple-500/30";
    case "cyan":
      return "bg-cyan-500/20 text-cyan-400 border-cyan-500/30";
    default:
      return "bg-sa-text-dim/20 text-sa-text-dim border-sa-border/30";
  }
}

/**
 * Search icon SVG
 */
const SearchIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

/**
 * Trace icon SVG
 */
const TraceIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M2 12h4l3 9 4-18 3 9h4" />
  </svg>
);

/**
 * Observable row component
 */
const ObservableRow = memo(
  ({
    id,
    observable,
    isHighlighted,
    onTrace,
    compact,
  }: {
    id: string;
    observable: Observable;
    isHighlighted: boolean;
    onTrace: () => void;
    compact?: boolean;
  }) => {
    const typeColor = getTypeColor(observable.type);
    const typeGlyph = getTypeGlyph(observable.type);
    const badgeColor = getBadgeColorClasses(observable.ui_hint?.badge_color);

    return (
      <div
        className={`
          px-2 py-1.5 rounded transition-colors
          ${isHighlighted ? "bg-primary/20 ring-1 ring-primary/50" : "hover:bg-sa-surface-hover"}
        `}
      >
        <div className="flex items-center gap-2">
          {/* Type badge */}
          <span className={`w-5 h-5 flex items-center justify-center rounded text-xs ${typeColor}`}>
            {typeGlyph}
          </span>

          {/* Observable ID */}
          <code className="text-xs font-mono text-sa-text truncate flex-1">{id}</code>

          {/* UI hint badge */}
          {observable.ui_hint && (
            <span className={`px-1.5 py-0.5 text-[10px] rounded border ${badgeColor}`}>
              {observable.ui_hint.glyph || "badge"}
            </span>
          )}

          {/* Trace button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onTrace();
            }}
            className="p-1 text-sa-text-muted hover:text-cyan-400 hover:bg-sa-surface-hover rounded transition-colors"
            title="Trace observable"
            aria-label={`Trace ${id}`}
          >
            <TraceIcon />
          </button>
        </div>

        {/* Observable details (non-compact) */}
        {!compact && (
          <div className="mt-1 ml-7 text-[11px] text-sa-text-muted space-y-0.5">
            {/* Type and unit */}
            <div>
              <span className={typeColor}>{observable.type}</span>
              {observable.unit && <span className="text-sa-text-dim"> ({observable.unit})</span>}
            </div>

            {/* Time domain */}
            {observable.time_domain && (
              <div>
                time: <span className="text-sa-warning">{observable.time_domain}</span>
              </div>
            )}

            {/* Source reference */}
            {observable.source_ref && (
              <div>
                source: <span className="text-cyan-400">{observable.source_ref}</span>
              </div>
            )}

            {/* Gate binding */}
            {observable.gate_binding && (
              <div>
                gate: <span className="text-purple-400">{observable.gate_binding.gate_id}</span>
                <span className="text-sa-text-muted">
                  {" "}
                  ({observable.gate_binding.approval_type})
                </span>
              </div>
            )}

            {/* Description */}
            {observable.description && (
              <div className="text-sa-text-muted truncate" title={observable.description}>
                {observable.description}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

ObservableRow.displayName = "ObservableRow";

/**
 * Observables Inspector Panel
 *
 * Displays observable definitions with filtering and trace actions.
 */
export const ObservablesInspectorPanel = memo(
  ({ observables, compact = false, className = "" }: ObservablesInspectorPanelProps) => {
    const highlightContext = useHighlightOptional();
    const [searchQuery, setSearchQuery] = useState("");
    const [typeFilter, setTypeFilter] = useState<string | null>(null);

    // Get all unique observable types for filter
    const observableTypes = useMemo(() => {
      if (!observables) return [];
      const types = new Set<string>();
      Object.values(observables).forEach((obs) => types.add(obs.type));
      return Array.from(types).sort();
    }, [observables]);

    // Filter and sort observables
    const filteredObservables = useMemo(() => {
      if (!observables) return [];

      const entries = Object.entries(observables);

      return entries
        .filter(([id, obs]) => {
          // Type filter
          if (typeFilter && obs.type !== typeFilter) return false;

          // Search filter
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchesId = id.toLowerCase().includes(query);
            const matchesDesc = obs.description?.toLowerCase().includes(query);
            const matchesSource = obs.source_ref?.toLowerCase().includes(query);
            if (!matchesId && !matchesDesc && !matchesSource) return false;
          }

          return true;
        })
        .sort(([idA], [idB]) => idA.localeCompare(idB));
    }, [observables, searchQuery, typeFilter]);

    // Handle trace action
    const handleTrace = useCallback(
      (id: string, observable: Observable) => {
        if (!highlightContext) return;

        // Build node IDs from source_ref or gate_binding
        const nodeIds: string[] = [];

        if (observable.source_ref) {
          // Parse source_ref to get node ID
          const parts = observable.source_ref.split(".");
          if (parts.length >= 2) {
            const refId = parts.slice(1).join(".");
            // Try all possible prefixes
            nodeIds.push(`internal_${refId}`);
            nodeIds.push(`gate_${refId}`);
            nodeIds.push(`source_${refId}`);
            nodeIds.push(`sink_${refId}`);
            nodeIds.push(`external_${refId}`);
          }
        }

        if (observable.gate_binding) {
          nodeIds.push(`gate_${observable.gate_binding.gate_id}`);
        }

        highlightContext.highlightObservables([id], nodeIds);
      },
      [highlightContext],
    );

    if (!observables || Object.keys(observables).length === 0) {
      return (
        <div className={`text-sm text-sa-text-muted p-3 ${className}`}>No observables defined</div>
      );
    }

    return (
      <div className={`flex flex-col ${className}`}>
        {/* Header */}
        <div className="px-3 py-2 border-b border-sa-border">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-sa-text">Observables</h3>
            <span className="text-xs text-sa-text-muted">
              {filteredObservables.length} of {Object.keys(observables).length}
            </span>
          </div>
        </div>

        {/* Search and filter */}
        {!compact && (
          <div className="px-2 py-2 border-b border-sa-border space-y-2">
            {/* Search input */}
            <div className="relative">
              <div className="absolute left-2 top-1/2 -translate-y-1/2 text-sa-text-muted">
                <SearchIcon />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search observables..."
                className="w-full pl-8 pr-2 py-1.5 text-xs bg-sa-surface border border-sa-border rounded
                  text-sa-text placeholder-sa-text-dim focus:outline-none focus:border-sa-border"
              />
            </div>

            {/* Type filter */}
            {observableTypes.length > 1 && (
              <div className="flex items-center gap-1 flex-wrap">
                <button
                  type="button"
                  onClick={() => setTypeFilter(null)}
                  className={`px-2 py-0.5 text-[10px] rounded transition-colors
                    ${typeFilter === null ? "bg-sa-text-dim text-white" : "bg-sa-surface text-sa-text-dim hover:bg-sa-surface-hover"}`}
                >
                  All
                </button>
                {observableTypes.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setTypeFilter(type === typeFilter ? null : type)}
                    className={`px-2 py-0.5 text-[10px] rounded transition-colors
                      ${typeFilter === type ? "bg-sa-text-dim text-white" : "bg-sa-surface text-sa-text-dim hover:bg-sa-surface-hover"}`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Observable list */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredObservables.length === 0 ? (
            <div className="text-xs text-sa-text-muted text-center py-4">
              No matching observables
            </div>
          ) : (
            filteredObservables.map(([id, observable]) => {
              const isHighlighted = highlightContext?.isObservableHighlighted(id) ?? false;

              return (
                <ObservableRow
                  key={id}
                  id={id}
                  observable={observable}
                  isHighlighted={isHighlighted}
                  onTrace={() => handleTrace(id, observable)}
                  compact={compact}
                />
              );
            })
          )}
        </div>
      </div>
    );
  },
);

ObservablesInspectorPanel.displayName = "ObservablesInspectorPanel";
